import { useState, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns/fp';
import {
	get,
	omit,
	flow,
	find,
	getOr,
	keyBy,
	pickBy,
	mapValues,
	replace,
	isEmpty,
	includes,
	capitalize,
} from 'lodash/fp';
import type { ProfileV1 } from '@fshr/microservices/profile';
import type { ContentV1 } from '@fshr/microservices/content';
import { useTealium } from '../context/tealium-context';
import { BookingV1 } from '@fshr/microservices/booking';
import { Oj } from '@fshr/shared/types';

const SESSION_KEYS = ['appUserId', 'clientId', 'sessionToken'] as const;

interface WebChatOptions {
	hideFloatingChatButton?: boolean;
	onReady?: () => void;
	onDestroy?: () => void;
	onWidgetOpen?: () => void;
	onWidgetClose?: () => void;
	onMessageSent?: (message: any) => void;
	generateAdditionalMetadata?: () => Record<string, any>;
}

interface UseWebChatProps {
	webChatContent?: ContentV1.WebChatResponse;
	profile?: ProfileV1.ProfileSummaryResponse;
	bookingId?: string;
	ojBooking?: Oj.Booking;
	bffBooking?: BookingV1.RoomReservation;
	localeIsoCode?: string;
	options?: WebChatOptions;
}

interface UseWebChatReturn {
	enabled: boolean;
	initialized: boolean;
	openWebChat: () => void;
	setPredefinedMessage: (message: string) => void;
}

interface UserData {
	givenName?: string;
	surname?: string;
	email?: string;
	phone?: string;
	jwt?: string;
	externalId?: string;
	title?: string;
}

interface ReservationData {
	confirmationNumber?: string;
	checkIn?: string;
	checkOut?: string;
}

function getFieldValue(name: string, message: any) {
	return flow(
		getOr([], 'fields'),
		find((field: { name?: string }) => field?.name === name),
		get(name === 'email' ? name : 'text')
	)(message);
}

const clearSmoochSession = (integrationId?: string) => {
	if (!integrationId) return;

	SESSION_KEYS.forEach(key => {
		sessionStorage.removeItem(`${integrationId}.${key}`);
	});
};

const extractUserData = (
	profile: ProfileV1.ProfileSummaryResponse | undefined,
	ojCustomer: Oj.CustomerResult | undefined,
	bffCustomer: BookingV1.RoomReservationCustomer | undefined
): UserData => {
	const extractTitle = () => {
		const rawTitle = !isEmpty(profile)
			? profile.title
			: bffCustomer?.name?.title || ojCustomer?.name?.title;
		if (!rawTitle) return undefined;

		const capitalizedTitle = capitalize(rawTitle);
		return includes('.')(capitalizedTitle) ? capitalizedTitle : `${capitalizedTitle}.`;
	};

	return {
		givenName: profile?.firstName || bffCustomer?.name?.firstName || ojCustomer?.name?.firstName,
		surname: profile?.surname || bffCustomer?.name?.surname || ojCustomer?.name?.surname,
		email: profile?.email || bffCustomer?.email?.email || ojCustomer?.emails?.[0]?.email,
		phone: profile?.phone || bffCustomer?.phone?.number || ojCustomer?.phones?.[0]?.number,
		jwt: profile?.webChatToken || bffCustomer?.webChatToken || ojCustomer?.webChatToken,
		externalId: profile?.orsNameId || bffCustomer?.orsNameId || ojCustomer?.orsNameId,
		title: extractTitle(),
	};
};

const getIframeElement = (selector: string, type: 'id' | 'class' = 'id'): HTMLElement | null => {
	const iframe = document.getElementById('web-messenger-container') as HTMLIFrameElement | null;
	const iframeDocument = iframe?.contentWindow?.document;
	if (!iframeDocument) return null;

	if (type === 'id') {
		return iframeDocument.getElementById(selector) as HTMLElement;
	} else {
		return iframeDocument.getElementsByClassName(selector)[0] as HTMLElement;
	}
};

const buildChatMetadata = (
	baseMetadata: Record<string, any>,
	propertyData: { name?: string; code?: string },
	reservationData: ReservationData,
	additionalMetadata: Record<string, any>
): Record<string, any> => {
	const metadata: any = {
		property_name: propertyData.name,
		property_code: propertyData.code,
		webpage_url: window.location.href,
		...baseMetadata,
	};

	if (reservationData.confirmationNumber) {
		metadata.propertyName = propertyData.name;
		metadata.confirmationNumber = reservationData.confirmationNumber;

		const dates = [reservationData.checkIn, reservationData.checkOut]
			.filter((date): date is string => Boolean(date))
			.map(parseISO)
			.map(format("MMM d',' yyyy"))
			.join(' - ');
		metadata.stayDates = dates;
	}

	return { ...metadata, ...additionalMetadata };
};

export function useWebChat({
	webChatContent,
	profile,
	bookingId,
	ojBooking,
	bffBooking,
	localeIsoCode = 'en-US',
	options,
}: UseWebChatProps): UseWebChatReturn {
	const [initialized, setInitialized] = useState(false);
	const [smoochInstance, setSmoochInstance] = useState<any>(null);
	const { trackLink } = useTealium();
	const authInProgressRef = useRef(false);

	const enabled = webChatContent?.enabled ?? false;
	const integrationId = webChatContent?.integrationId;
	const propertyCode = webChatContent?.propertyInfo?.propertyCode;
	const businessIconUrl = webChatContent?.propertyInfo?.image?.url;
	const shouldHideFloatingChatButton = options?.hideFloatingChatButton ?? false;

	const hasUserData =
		!profile?.dataRedacted && (!isEmpty(profile) || !isEmpty(ojBooking) || !isEmpty(bffBooking));

	//const reservation: ReservationData | undefined = ojBooking?.hotelProducts?.[0] || bffBooking;

	const reservationId =
		bffBooking?.orsConfirmationNumber || ojBooking?.hotelProducts?.[0]?.reservationId;

	const userData = extractUserData(profile, ojBooking?.customers?.[0], bffBooking?.customer);
	const { givenName, surname, email, phone, jwt, externalId, title } = userData;

	const propertyNameRaw = webChatContent?.propertyInfo?.name ?? '';
	const propertyNameIncludesHtml = webChatContent?.propertyInfo?.nameIncludesHtml ?? false;
	const propertyName = propertyNameIncludesHtml
		? replace(/(<([^>]+)>)/gi, '')(propertyNameRaw)
		: propertyNameRaw;

	const getCustomText = () =>
		flow(
			getOr([], 'translations'),
			keyBy('key'),
			mapValues(({ value }: { value: string }) => value)
		)(webChatContent);

	const smoochProperties = {
		businessName: propertyName,
		browserStorage: 'sessionStorage' as const,
		businessIconUrl,
		locale: localeIsoCode,
		customText: getCustomText(),
		integrationOrder: [],
		customColors: {
			brandColor: '000000',
			conversationColor: '000000',
			actionColor: '000000',
		},
		canUserSeeConversationList: false,
		menuItems: {
			imageUpload: true,
			fileUpload: false,
			shareLocation: true,
		},
		prechatCapture: {
			enabled: !hasUserData, // Only show for anonymous users
			enableEmailLinking: true,
			fields: [
				{
					type: 'text',
					name: 'firstName',
					label: 'First Name',
					placeholder: 'Enter your first name',
				},
				{
					type: 'text',
					name: 'lastName',
					label: 'Last Name',
					placeholder: 'Enter your last name',
				},
				{
					type: 'email',
					name: 'email',
					label: 'Email Address',
					placeholder: 'Enter your email',
				},
			],
		},
	};

	const handleSmoochEvents = () => {
		if (!smoochInstance) return;
		const emptyFunction = () => undefined;
		smoochInstance.on('destroy', options?.onDestroy || emptyFunction);
		smoochInstance.on('widget:opened', options?.onWidgetOpen || emptyFunction);
		smoochInstance.on('widget:closed', options?.onWidgetClose || emptyFunction);
		smoochInstance.on('message:sent', options?.onMessageSent || emptyFunction);
	};

	const fixPropertyNameWithHtml = () => {
		const propertyNameDiv = getIframeElement('app-name', 'class');
		if (propertyNameDiv) {
			propertyNameDiv.innerHTML = propertyNameRaw;
		}
	};

	const hideButton = () => {
		const webChatButton = getIframeElement('messenger-button', 'id');
		if (webChatButton) {
			webChatButton.style.visibility = 'hidden';
		}
	};

	const applyFloatingChatCustomizations = () => {
		if (!shouldHideFloatingChatButton) return;

		hideButton();
	};

	const handleSmoochEnabled = () => {
		applyFloatingChatCustomizations();
		if (propertyNameIncludesHtml) fixPropertyNameWithHtml();
		if (options?.onReady) options.onReady();
		handleSmoochEvents();
		setInitialized(true);
	};

	const beforeDisplay = (message: any, _data: any) => {
		if (message.deleted) return null;
		if (!message.metadata) return null;
		return message.metadata?.property_code === propertyCode ? message : null;
	};

	const beforeSend = (message: any, data: any) => {
		if (message?.type === 'formResponse') {
			const givenName = getFieldValue('firstName', message);
			const surname = getFieldValue('lastName', message);
			const email = getFieldValue('email', message);

			smoochInstance.updateUser({
				givenName,
				surname,
				email,
			});
		}

		const metadata = buildChatMetadata(
			data?.metadata || {},
			{ name: propertyName, code: propertyCode },
			reservationId
				? {
						confirmationNumber: reservationId,
						checkIn: bffBooking?.checkInDate || ojBooking?.hotelProducts?.[0]?.checkInDate,
						checkOut: bffBooking?.checkOutDate || ojBooking?.hotelProducts?.[0]?.checkOutDate,
					}
				: {},
			options?.generateAdditionalMetadata?.() || {}
		);

		return { ...message, metadata };
	};

	const openWebChat = () => {
		if (!smoochInstance || !initialized) return;

		if (smoochInstance.open && !smoochInstance.isOpened?.()) {
			smoochInstance.open();
			trackLink('chat_start');
		}
	};

	const setPredefinedMessage = (message: string) => {
		if (!smoochInstance || !initialized) return;

		if (smoochInstance.setPredefinedMessage && message) {
			smoochInstance.setPredefinedMessage(message);
		}
	};

	const updateConversationDisplayName = (displayName: string) => {
		return (conversations: any[]) => {
			const conversation = conversations[0];
			const conversationId = get('_id')(conversation);
			if (conversationId && smoochInstance) {
				smoochInstance.updateConversation(conversationId, { displayName });
			}
		};
	};

	const updateUserData = () => {
		const updateUser = flow(
			pickBy(Boolean),
			data => (flow(get('metadata'), isEmpty)(data) ? omit(['metadata'], data) : data),
			data => {
				if (!isEmpty(data)) {
					smoochInstance.updateUser(data);
				}
			}
		);

		updateUser({
			givenName,
			surname,
			email,
			metadata: pickBy(Boolean)({
				'profile.id': externalId,
				'profile.title': title,
				'profile.phone': phone,
			}),
		});
	};

	const handleAfterDestroy = () => {
		clearSmoochSession(integrationId);
	};

	const handleAfterLogin = (response: any) => {
		const conversations = response?.conversations || [];
		updateConversationDisplayName(propertyName)(conversations);
		updateUserData();
	};

	const handleInitSmooch = () => {
		if (!smoochInstance?.init || !enabled || !integrationId) return Promise.resolve();

		return smoochInstance
			.init({ ...smoochProperties, integrationId })
			.then(handleSmoochEnabled)
			.catch(console.log);
	};

	const handleDestroySmooch = () => {
		if (!smoochInstance?.destroy) return;

		setInitialized(false);
		smoochInstance.destroy();
	};

	const withAuthGuard = async (operation: () => Promise<void>) => {
		authInProgressRef.current = true;
		try {
			await operation();
		} finally {
			authInProgressRef.current = false;
		}
	};

	const reinitializeSmooch = async () => {
		handleDestroySmooch();
		handleAfterDestroy();
		await handleInitSmooch();
	};

	const handleUserLogout = () => {
		withAuthGuard(reinitializeSmooch);
	};

	const handleSignInWithoutJWT = () => {
		withAuthGuard(async () => {
			await reinitializeSmooch();
			updateUserData();
		});
	};

	const handleSignInWithJWT = () => {
		const performLogin = () => {
			return smoochInstance.login(externalId, jwt).then(handleAfterLogin).catch(console.log);
		};

		const smoochUser = smoochInstance.getUser();
		const metadataExternalId = smoochUser?.metadata?.['profile.id'];
		const isSameUser = metadataExternalId === externalId;

		if (!isSameUser) {
			withAuthGuard(async () => {
				await reinitializeSmooch();
				await performLogin();
			});
		} else {
			withAuthGuard(performLogin);
		}
	};

	useEffect(() => {
		if (typeof window === 'undefined') return;

		import('smooch')
			.then(module => {
				setSmoochInstance(module.default);
			})
			.catch(console.log);
	}, []);

	useEffect(() => {
		handleInitSmooch();

		return () => {
			handleDestroySmooch();
		};
	}, [enabled, integrationId, smoochInstance]);

	useEffect(() => {
		if (!smoochInstance || !initialized) return;

		if (smoochInstance.setDelegate) {
			smoochInstance.setDelegate({ beforeDisplay, beforeSend });
		}
	}, [initialized, smoochInstance, bookingId, propertyCode]);

	useEffect(() => {
		if (!smoochInstance?.getUser || !initialized || authInProgressRef.current) return;

		const smoochUser = smoochInstance.getUser();
		const currentLoggedInId = smoochUser?.externalId;
		const metadataExternalId = smoochUser?.metadata?.['profile.id'];

		// SCENARIO 1: User logs out (hasUserData: true → false)
		if (!hasUserData && (currentLoggedInId || metadataExternalId)) {
			handleUserLogout();
			return;
		}

		// SCENARIO 2: User signs in (JWT is not available, but session exists)
		if (
			hasUserData &&
			externalId &&
			!jwt &&
			!currentLoggedInId &&
			metadataExternalId !== externalId
		) {
			handleSignInWithoutJWT();
			return;
		}

		// SCENARIO 3: User signs in (JWT is available)
		if (hasUserData && externalId && jwt) {
			handleSignInWithJWT();
			return;
		}
	}, [initialized, smoochInstance, hasUserData, externalId, jwt, integrationId]);

	return {
		enabled: enabled && !!smoochInstance,
		initialized,
		openWebChat,
		setPredefinedMessage,
	};
}
